import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBold, faCode, faListUl, faRotateRight, faUnderline } from '@fortawesome/free-solid-svg-icons'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore'
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage'
import { useLocation, useNavigate } from 'react-router-dom'
import { db, storage } from '../firebase'

const normalizeText = (value) => String(value || '').trim()
const MAX_ASSIGNMENT_ATTACHMENT_SIZE = 20 * 1024 * 1024

const sanitizeStorageFileName = (value = 'file') => (
  String(value || 'file').replace(/[^a-zA-Z0-9._-]+/g, '_')
)

const formatFileSize = (value) => {
  const size = Number(value) || 0
  if (size <= 0) return ''
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  if (size >= 1024) return `${Math.round(size / 1024)} KB`
  return `${size} B`
}

const normalizeKey = (value) => normalizeText(value)
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const normalizeEmail = (value) => normalizeText(value).toLowerCase()

const chunkArray = (items, size) => {
  const chunks = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

const formatDate = (value) => {
  if (!value) return 'No due date'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatDateTime = (value) => {
  if (!value) return 'Unknown'
  if (typeof value?.toMillis === 'function') {
    return new Date(value.toMillis()).toLocaleString()
  }
  if (typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000).toLocaleString()
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString()
}

const normalizeTimestamp = (value) => {
  if (!value) return 0
  if (typeof value?.toMillis === 'function') return value.toMillis()
  if (typeof value?.seconds === 'number') return value.seconds * 1000
  return new Date(value).getTime() || 0
}

const isOverdue = (value) => {
  if (!value) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDate = new Date(value)
  dueDate.setHours(0, 0, 0, 0)
  return dueDate < today
}

const isDueSoon = (value) => {
  if (!value) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDate = new Date(value)
  dueDate.setHours(0, 0, 0, 0)
  const difference = dueDate.getTime() - today.getTime()
  return difference >= 0 && difference <= 7 * 24 * 60 * 60 * 1000
}

const getSubmissionStatus = (submission = {}) => (
  normalizeKey(submission.status || submission.reviewStatus || 'submitted')
)

const sanitizeRichTextHtml = (input) => {
  if (!input) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(input, 'text/html')
  const allowedTags = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'code', 'pre', 'br', 'p', 'div', 'ul', 'ol', 'li', 'blockquote', 'span'])

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase()
        if (!allowedTags.has(tag)) {
          const fragment = doc.createDocumentFragment()
          while (child.firstChild) fragment.appendChild(child.firstChild)
          child.replaceWith(fragment)
          return
        }
        Array.from(child.attributes).forEach((attr) => child.removeAttribute(attr.name))
        walk(child)
      } else if (child.nodeType === Node.COMMENT_NODE) {
        child.remove()
      }
    })
  }

  walk(doc.body)
  return doc.body.innerHTML
}

const getPlainTextFromHtml = (input) => {
  if (!input) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(input, 'text/html')
  return (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim()
}

const escapeHtml = (input) => String(input || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;')

const assignmentTypeOptions = [
  {
    value: 'questions_text',
    label: 'Questions to answer non code',
    responseMode: 'text',
    supportsPromptItems: true
  },
  {
    value: 'questions_code',
    label: 'Question to answer code submission',
    responseMode: 'code',
    supportsPromptItems: true
  },
  {
    value: 'code_link',
    label: 'Code submission - GitHub link',
    responseMode: 'github',
    supportsPromptItems: false
  },
  {
    value: 'code_paste',
    label: 'Code submission - paste code',
    responseMode: 'code',
    supportsPromptItems: false
  },
  {
    value: 'project_demo',
    label: 'Project link or demo',
    responseMode: 'project',
    supportsPromptItems: false
  }
]

const getAssignmentTypeConfig = (value) => (
  assignmentTypeOptions.find((option) => option.value === value) ||
  assignmentTypeOptions.find((option) => option.value === 'code_link')
)

const getAssignmentTypeLabel = (assignment = {}) => (
  assignment.assignmentTypeLabel
  || assignment.assignmentType
  || 'task'
)

const getAssignmentWeekLabel = (assignment = {}) => (
  normalizeText(assignment.assignmentWeekLabel)
  || (normalizeText(assignment.assignmentWeek) ? `Week ${normalizeText(assignment.assignmentWeek)}` : '')
)

const getAssignmentAttachment = (assignment = {}) => {
  const attachment = assignment?.assignmentAttachment
  if (!attachment || !normalizeText(attachment.url || attachment.downloadURL)) return null

  return {
    name: normalizeText(attachment.name) || 'Assignment file',
    url: normalizeText(attachment.url || attachment.downloadURL),
    path: normalizeText(attachment.path || attachment.fullPath),
    size: Number(attachment.size) || 0,
    contentType: normalizeText(attachment.contentType || attachment.type),
    uploadedAtMs: Number(attachment.uploadedAtMs) || 0
  }
}

const buildAssignmentSubmissionReadId = (instructorId, submissionId) => (
  `${instructorId}_${submissionId}`.replace(/[^a-zA-Z0-9_-]/g, '_')
)

const ASSIGNMENT_ASSESSMENT_ENDPOINT = process.env.REACT_APP_ASSIGNMENT_ASSESSMENT_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/generateAssignmentAssessment'

let promptCounter = 0
const createPromptItem = (html = '') => ({
  id: `assignment-prompt-${Date.now()}-${promptCounter++}`,
  html
})

const normalizePromptItemsForEditor = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return [createPromptItem('')]
  return items.map((item) => (
    typeof item === 'string'
      ? createPromptItem(item)
      : {
          id: item?.id || createPromptItem('').id,
          html: item?.html || ''
        }
  ))
}

const getSubmissionLinks = (submission = {}) => (
  [
    {
      label: 'GitHub repository',
      value: submission.githubLink || submission.githubUrl || submission.repositoryUrl || submission.repoLink
    },
    {
      label: 'Project link',
      value: submission.projectLink || submission.projectUrl || submission.liveLink || submission.liveUrl
    },
    {
      label: 'Demo video',
      value: submission.videoLink || submission.videoUrl || submission.demoVideoLink || submission.demoUrl
    }
  ].filter((item) => normalizeText(item.value))
)

const getSubmissionPreview = (submission = {}) => {
  const answers = Array.isArray(submission.responseItems)
    ? submission.responseItems.map((item) => normalizeText(item)).filter(Boolean)
    : []

  if (answers.length > 0) {
    return answers.slice(0, 2).join(' ')
  }

  return normalizeText(submission.responseText || submission.answerText || submission.notes || '')
}

const getAssignmentDisplayHtml = (assignment = {}) => {
  const instructionsHtml = sanitizeRichTextHtml(assignment.instructionsHtml || assignment.instructions || '')
  if (instructionsHtml) return instructionsHtml

  if (Array.isArray(assignment.promptItems) && assignment.promptItems.length > 0) {
    return assignment.promptItems
      .map((prompt, index) => (
        `<div class="assignmentPromptViewItem"><span>Question ${index + 1}</span>${sanitizeRichTextHtml(prompt)}</div>`
      ))
      .join('')
  }

  return '<p>No instructions added.</p>'
}

const normalizeAssessmentList = (items = []) => (
  (Array.isArray(items) ? items : []).filter(Boolean)
)

const renderAssessmentScore = (value, outOf = 5) => {
  const score = Number(value)
  if (!Number.isFinite(score)) return 'Not scored'
  return `${score}/${outOf}`
}

const getAssessmentScorePercent = (value, outOf = 5) => {
  const score = Number(value)
  const total = Number(outOf)
  if (!Number.isFinite(score) || !Number.isFinite(total) || total <= 0) return 0
  return Math.max(0, Math.min(100, (score / total) * 100))
}

const getAssessmentChartItems = (items = [], limit = 6) => (
  normalizeAssessmentList(items)
    .map((item, index) => {
      const label = normalizeText(item?.label || item?.title || item?.promptLabel || `Item ${index + 1}`)
      const score = Number(item?.score)
      const outOf = Number(item?.outOf || 5)

      if (!Number.isFinite(score) || !Number.isFinite(outOf) || outOf <= 0) return null

      return {
        id: item?.id || label || `chart-${index}`,
        label,
        shortLabel: label.length > 16 ? `${label.slice(0, 16).trim()}...` : label,
        score,
        outOf,
        percent: getAssessmentScorePercent(score, outOf)
      }
    })
    .filter(Boolean)
    .slice(0, limit)
)

const getSubmissionResponseItems = (submission = {}) => (
  Array.isArray(submission.responseItems)
    ? submission.responseItems.map((item) => normalizeText(item)).filter(Boolean)
    : []
)

const getSubmissionResponseHtmlItems = (submission = {}) => (
  Array.isArray(submission.responseItemsHtml)
    ? submission.responseItemsHtml.map((item) => sanitizeRichTextHtml(item)).filter(Boolean)
    : []
)

const getAssignmentAudienceIds = (assignment = {}, classMemberships = [], profilesById = {}) => {
  const explicitStudentIds = Array.isArray(assignment.studentIds)
    ? Array.from(new Set(assignment.studentIds.map((value) => String(value)).filter(Boolean)))
    : []

  if (explicitStudentIds.length > 0) return explicitStudentIds

  const assignmentCourseKeys = new Set([
    normalizeKey(assignment.courseName),
    normalizeKey(assignment.courseKey),
    normalizeKey(assignment.courseId)
  ].filter(Boolean))

  const rosterIds = classMemberships
    .map((student) => student.studentID || student.userId || student.id)
    .filter(Boolean)

  if (!assignmentCourseKeys.size) return Array.from(new Set(rosterIds.map(String)))

  const matchingRosterIds = classMemberships
    .filter((student) => {
      const studentId = student.studentID || student.userId || student.id
      const profile = profilesById[studentId] || {}
      const studentCourseKeys = new Set([
        normalizeKey(student.course),
        normalizeKey(student.courseName),
        normalizeKey(student.program),
        normalizeKey(profile.course),
        normalizeKey(profile.program)
      ].filter(Boolean))

      return Array.from(studentCourseKeys).some((key) => assignmentCourseKeys.has(key))
    })
    .map((student) => String(student.studentID || student.userId || student.id))
    .filter(Boolean)

  return matchingRosterIds.length ? Array.from(new Set(matchingRosterIds)) : Array.from(new Set(rosterIds.map(String)))
}

const getEditFormFromAssignment = (assignment) => ({
  title: assignment?.title || '',
  assignmentType: assignment?.assignmentType || 'code_link',
  dueDate: assignment?.dueDate || '',
  resourceLink: assignment?.resourceLink || '',
  instructions: assignment?.instructionsHtml || assignment?.instructions || '',
  promptItems: normalizePromptItemsForEditor(assignment?.promptItems),
  assignmentAttachment: getAssignmentAttachment(assignment),
  nextAttachmentFile: null,
  removeAttachment: false
})

function AssignmentsComp() {
  const auth = getAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [loadMessage, setLoadMessage] = useState('')
  const [currentUserId, setCurrentUserId] = useState('')
  const [assignments, setAssignments] = useState([])
  const [submissions, setSubmissions] = useState([])
  const [assignmentReads, setAssignmentReads] = useState([])
  const [classMemberships, setClassMemberships] = useState([])
  const [studentDirectory, setStudentDirectory] = useState({ byId: {}, byEmail: {} })
  const [courseFilter, setCourseFilter] = useState('All')
  const [statusFilter, setStatusFilter] = useState('All')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAssignmentId, setSelectedAssignmentId] = useState('')
  const [isAssignmentOpen, setIsAssignmentOpen] = useState(false)
  const [activeAssignmentTab, setActiveAssignmentTab] = useState('overview')
  const [selectedSubmissionId, setSelectedSubmissionId] = useState('')
  const [reviewNotice, setReviewNotice] = useState('')
  const [assessmentBySubmission, setAssessmentBySubmission] = useState({})
  const [assessmentLoading, setAssessmentLoading] = useState(false)
  const [assessmentError, setAssessmentError] = useState('')
  const [runningAssessment, setRunningAssessment] = useState(false)
  const [isAssessmentOpen, setIsAssessmentOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [savingEdit, setSavingEdit] = useState(false)
  const [editNotice, setEditNotice] = useState('')
  const [editForm, setEditForm] = useState(getEditFormFromAssignment(null))
  const instructionEditorRef = useRef(null)
  const promptEditorRefs = useRef({})
  const editAttachmentInputRef = useRef(null)
  const handledLocationStateRef = useRef('')

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCurrentUserId('')
        setLoading(false)
        setLoadMessage('Please sign in to view assignments.')
        return
      }

      setCurrentUserId(user.uid)
      setLoading(true)
      setLoadMessage('')

      try {
        const [assignmentsSnapshot, classesSnapshot] = await Promise.all([
          getDocs(query(collection(db, 'courseAssignments'), where('instructorId', '==', user.uid))),
          getDocs(query(collection(db, 'allclasses'), where('instructor', '==', user.uid)))
        ])

        const assignmentList = assignmentsSnapshot.docs
          .map((assignmentDoc) => ({ id: assignmentDoc.id, ...assignmentDoc.data() }))
          .sort((first, second) => String(second.createdAt || '').localeCompare(String(first.createdAt || '')))

        const assignmentIds = assignmentList.map((assignment) => assignment.id)
        const submissionSnapshots = assignmentIds.length > 0
          ? await Promise.all(
            chunkArray(assignmentIds, 10).map((assignmentChunk) => (
              getDocs(query(collection(db, 'assignmentSubmissions'), where('assignmentId', 'in', assignmentChunk)))
            ))
          )
          : []
        const readSnapshots = assignmentIds.length > 0
          ? await Promise.all(
            chunkArray(assignmentIds, 10).map((assignmentChunk) => (
              getDocs(query(collection(db, 'studentAssignmentReads'), where('assignmentId', 'in', assignmentChunk)))
            ))
          )
          : []

        const classIds = classesSnapshot.docs
          .map((classDoc) => classDoc.data()?.roomID || classDoc.id)
          .filter(Boolean)

        const membershipSnapshots = await Promise.all(
          classIds.map((classId) => getDocs(query(collection(db, 'StudentsInClass'), where('classID', '==', classId))))
        )

        const memberships = membershipSnapshots.flatMap((snapshot) => (
          snapshot.docs.map((studentDoc) => ({ id: studentDoc.id, ...studentDoc.data() }))
        ))

        const studentIds = Array.from(new Set(
          memberships
            .map((student) => student.studentID || student.userId || student.id)
            .concat(assignmentList.flatMap((assignment) => assignment.studentIds || []))
            .filter(Boolean)
        ))

        const profileSnapshots = studentIds.length > 0
          ? await Promise.all(
            chunkArray(studentIds, 10).map((studentChunk) => (
              getDocs(query(collection(db, 'users'), where('userId', 'in', studentChunk)))
            ))
          )
          : []

        const byId = {}
        const byEmail = {}

        memberships.forEach((student) => {
          const studentId = student.studentID || student.userId || student.id
          const studentEmail = normalizeEmail(student.studentEmail || student.email)
          const profile = {
            studentId,
            name: student.name || student.studentEmail || 'Student',
            email: student.studentEmail || student.email || '',
            avatar: student.profilePic || '',
            course: student.course || ''
          }

          if (studentId) byId[studentId] = { ...(byId[studentId] || {}), ...profile }
          if (studentEmail) byEmail[studentEmail] = { ...(byEmail[studentEmail] || {}), ...profile }
        })

        profileSnapshots.forEach((snapshot) => {
          snapshot.docs.forEach((studentDoc) => {
            const profile = studentDoc.data() || {}
            const studentId = profile.userId || studentDoc.id
            const studentEmail = normalizeEmail(profile.userEmail || profile.email)
            const normalizedProfile = {
              studentId,
              name: profile.bio || profile.name || byId[studentId]?.name || 'Student',
              email: profile.userEmail || profile.email || byId[studentId]?.email || '',
              avatar: profile.profilePic || byId[studentId]?.avatar || '',
              course: profile.course || byId[studentId]?.course || ''
            }

            if (studentId) byId[studentId] = { ...(byId[studentId] || {}), ...normalizedProfile }
            if (studentEmail) byEmail[studentEmail] = { ...(byEmail[studentEmail] || {}), ...normalizedProfile }
          })
        })

        setAssignments(assignmentList)
        setClassMemberships(memberships)
        setSubmissions(submissionSnapshots.flatMap((snapshot) => (
          snapshot.docs.map((submissionDoc) => ({ id: submissionDoc.id, ...submissionDoc.data() }))
        )))
        setAssignmentReads(readSnapshots.flatMap((snapshot) => (
          snapshot.docs.map((readDoc) => ({ id: readDoc.id, ...readDoc.data() }))
        )))
        setStudentDirectory({ byId, byEmail })
      } catch (error) {
        console.error('Error loading assignment dashboard:', error)
        setLoadMessage('Could not load assignments right now. Please try again shortly.')
      } finally {
        setLoading(false)
      }
    })

    return () => unsubscribe()
  }, [auth])

  const submissionsByAssignment = useMemo(() => {
    const grouped = {}

    submissions.forEach((submission) => {
      if (!submission.assignmentId) return
      if (!grouped[submission.assignmentId]) grouped[submission.assignmentId] = []
      grouped[submission.assignmentId].push(submission)
    })

    Object.values(grouped).forEach((items) => {
      items.sort((first, second) => (
        new Date(second.updatedAtMs || second.submittedAtMs || second.createdAt || 0).getTime()
        - new Date(first.updatedAtMs || first.submittedAtMs || first.createdAt || 0).getTime()
      ))
    })

    return grouped
  }, [submissions])

  const readsByAssignment = useMemo(() => {
    const grouped = {}

    assignmentReads.forEach((readEntry) => {
      if (!readEntry.assignmentId) return
      if (!grouped[readEntry.assignmentId]) grouped[readEntry.assignmentId] = []
      grouped[readEntry.assignmentId].push(readEntry)
    })

    Object.values(grouped).forEach((items) => {
      items.sort((first, second) => (
        normalizeTimestamp(second.readAtMs || second.readAt)
        - normalizeTimestamp(first.readAtMs || first.readAt)
      ))
    })

    return grouped
  }, [assignmentReads])

  const assignmentRows = useMemo(() => (
    assignments.map((assignment) => {
      const assignmentSubmissions = submissionsByAssignment[assignment.id] || []
      const assignmentReadEntries = readsByAssignment[assignment.id] || []
      const audienceStudentIds = getAssignmentAudienceIds(assignment, classMemberships, studentDirectory.byId)
      const assignedCount = audienceStudentIds.length
      const reviewedCount = assignmentSubmissions.filter((submission) => getSubmissionStatus(submission).includes('review')).length
      const needsRevisionCount = assignmentSubmissions.filter((submission) => {
        const status = getSubmissionStatus(submission)
        return status.includes('revision') || status.includes('redo')
      }).length
      const submittedCount = assignmentSubmissions.length
      const pendingCount = Math.max(assignedCount - submittedCount, 0)
      const openedCount = assignmentReadEntries.length
      const unopenedCount = Math.max(assignedCount - openedCount, 0)
      const openedBy = assignmentReadEntries.map((readEntry) => {
        const studentId = String(readEntry.studentId || readEntry.studentID || readEntry.userId || '')
        const studentEmail = normalizeEmail(readEntry.studentEmail || readEntry.userEmail || '')
        const profile = (studentId && studentDirectory.byId[studentId]) || (studentEmail && studentDirectory.byEmail[studentEmail]) || {}

        return {
          ...readEntry,
          studentId,
          studentName: profile.name || readEntry.studentName || readEntry.name || readEntry.studentEmail || 'Student',
          studentEmail: profile.email || readEntry.studentEmail || readEntry.userEmail || '',
          studentAvatar: profile.avatar || '',
          studentCourse: profile.course || readEntry.courseName || ''
        }
      })

      return {
        ...assignment,
        assignedCount,
        audienceStudentIds,
        submittedCount,
        pendingCount,
        openedCount,
        unopenedCount,
        openedBy,
        reviewedCount,
        needsRevisionCount,
        submissions: assignmentSubmissions,
        overdue: isOverdue(assignment.dueDate),
        dueSoon: isDueSoon(assignment.dueDate)
      }
    })
  ), [assignments, classMemberships, readsByAssignment, studentDirectory.byEmail, studentDirectory.byId, submissionsByAssignment])

  const courses = useMemo(() => (
    Array.from(new Set(assignmentRows.map((assignment) => assignment.courseName).filter(Boolean))).sort()
  ), [assignmentRows])

  const dashboardStats = useMemo(() => ({
    total: assignmentRows.length,
    pendingReview: assignmentRows.reduce((total, assignment) => total + Math.max(assignment.submittedCount - assignment.reviewedCount, 0), 0),
    overdue: assignmentRows.filter((assignment) => assignment.overdue).length,
    dueSoon: assignmentRows.filter((assignment) => assignment.dueSoon).length
  }), [assignmentRows])

  const filteredAssignments = useMemo(() => {
    const term = normalizeKey(searchTerm)

    return assignmentRows.filter((assignment) => {
      const matchesCourse = courseFilter === 'All' || assignment.courseName === courseFilter
      const matchesStatus = (
        statusFilter === 'All' ||
        (statusFilter === 'Pending Review' && assignment.submittedCount > assignment.reviewedCount) ||
        (statusFilter === 'Overdue' && assignment.overdue) ||
        (statusFilter === 'Due Soon' && assignment.dueSoon) ||
        (statusFilter === 'Needs Revision' && assignment.needsRevisionCount > 0)
      )
      const matchesSearch = !term || [
        assignment.title,
        assignment.courseName,
        assignment.instructionsText || assignment.instructions,
        assignment.assignmentTypeLabel || assignment.assignmentType,
        getAssignmentWeekLabel(assignment),
        assignment.assignmentWeekTopic
      ].some((value) => normalizeKey(value).includes(term))

      return matchesCourse && matchesStatus && matchesSearch
    })
  }, [assignmentRows, courseFilter, statusFilter, searchTerm])

  useEffect(() => {
    if (!filteredAssignments.length) {
      setSelectedAssignmentId('')
      setIsEditing(false)
      setIsAssignmentOpen(false)
      return
    }

    if (!filteredAssignments.some((assignment) => assignment.id === selectedAssignmentId)) {
      setSelectedAssignmentId(filteredAssignments[0].id)
      setIsEditing(false)
      setIsAssignmentOpen(false)
    }
  }, [filteredAssignments, selectedAssignmentId])

  const selectedAssignment = useMemo(
    () => assignmentRows.find((assignment) => assignment.id === selectedAssignmentId) || null,
    [assignmentRows, selectedAssignmentId]
  )

  useEffect(() => {
    if (!selectedAssignmentId || !isAssignmentOpen) return

    let isMounted = true

    getDocs(query(collection(db, 'studentAssignmentReads'), where('assignmentId', '==', selectedAssignmentId)))
      .then((snapshot) => {
        if (!isMounted) return

        const nextReads = snapshot.docs.map((readDoc) => ({ id: readDoc.id, ...readDoc.data() }))
        setAssignmentReads((current) => {
          const otherReads = current.filter((readEntry) => readEntry.assignmentId !== selectedAssignmentId)
          return [...otherReads, ...nextReads]
        })
      })
      .catch((error) => {
        console.error('Error refreshing assignment read receipts:', error)
      })

    return () => {
      isMounted = false
    }
  }, [isAssignmentOpen, selectedAssignmentId])

  const selectedSubmission = useMemo(
    () => selectedAssignment?.submissions.find((submission) => submission.id === selectedSubmissionId) || null,
    [selectedAssignment, selectedSubmissionId]
  )

  const selectedAssessment = selectedSubmission?.id ? (assessmentBySubmission[selectedSubmission.id] || null) : null
  const hasCompletedAssessment = Boolean(
    selectedAssessment?.status === 'completed' && selectedAssessment?.assessment
  )
  const canRunNewReview = Boolean(selectedAssessment) && !runningAssessment
  const rubricChartItems = useMemo(
    () => getAssessmentChartItems(selectedAssessment?.assessment?.rubric),
    [selectedAssessment]
  )

  useEffect(() => {
    if (!selectedAssignment?.submissions?.length) {
      setSelectedSubmissionId('')
      return
    }

    if (selectedSubmissionId && !selectedAssignment.submissions.some((submission) => submission.id === selectedSubmissionId)) {
      setSelectedSubmissionId('')
    }
  }, [selectedAssignment, selectedSubmissionId])

  useEffect(() => {
    if (!selectedSubmission?.id) {
      setAssessmentLoading(false)
      setAssessmentError('')
      setIsAssessmentOpen(false)
      return
    }

    let isMounted = true
    setAssessmentLoading(true)
    setAssessmentError('')
    setIsAssessmentOpen(false)

    getDoc(doc(db, 'assignmentSubmissionAssessments', selectedSubmission.id))
      .then((snapshot) => {
        if (!isMounted) return
        setAssessmentBySubmission((current) => ({
          ...current,
          [selectedSubmission.id]: snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null
        }))
      })
      .catch((error) => {
        console.error('Error loading assignment assessment:', error)
        if (!isMounted) return
        setAssessmentError('Could not load the saved AI assessment for this submission.')
      })
      .finally(() => {
        if (isMounted) setAssessmentLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [selectedSubmission?.id])

  useEffect(() => {
    if (!runningAssessment) return undefined

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
      return ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [runningAssessment])

  useEffect(() => {
    const openAssignmentId = location.state?.openAssignmentId
    if (!openAssignmentId) return

    const locationStateKey = `${location.key}:${openAssignmentId}:${location.state?.openSubmissionId || ''}`
    if (handledLocationStateRef.current === locationStateKey) return
    if (!assignmentRows.some((assignment) => assignment.id === openAssignmentId)) return

    handledLocationStateRef.current = locationStateKey
    setCourseFilter('All')
    setStatusFilter('All')
    setSearchTerm('')
    setSelectedAssignmentId(openAssignmentId)
    setIsAssignmentOpen(true)
    setIsEditing(false)
    setEditNotice('')
    setReviewNotice('')

    if (location.state?.openSubmissionId) {
      setActiveAssignmentTab('submissions')
      setSelectedSubmissionId(location.state.openSubmissionId)
    } else {
      setActiveAssignmentTab('overview')
      setSelectedSubmissionId('')
    }
  }, [assignmentRows, location.key, location.state])

  useEffect(() => {
    if (!currentUserId || !selectedSubmission?.id || !selectedAssignment?.id) return

    setDoc(doc(db, 'assignmentSubmissionReads', buildAssignmentSubmissionReadId(currentUserId, selectedSubmission.id)), {
      instructorId: currentUserId,
      assignmentId: selectedAssignment.id,
      submissionId: selectedSubmission.id,
      readAt: serverTimestamp(),
      readAtMs: Date.now()
    }, { merge: true }).catch((error) => {
      console.error('Error marking assignment submission as read:', error)
    })
  }, [currentUserId, selectedAssignment?.id, selectedSubmission?.id])

  useEffect(() => {
    if (!selectedAssignment || !isEditing) return
    setEditForm(getEditFormFromAssignment(selectedAssignment))
    setEditNotice('')
    if (instructionEditorRef.current) {
      instructionEditorRef.current.innerHTML = selectedAssignment.instructionsHtml || selectedAssignment.instructions || ''
    }
    promptEditorRefs.current = {}
  }, [selectedAssignment, isEditing])

  const openStudentProfile = (studentLike = {}) => {
    const candidateId = studentLike.studentId || studentLike.studentID || studentLike.userId || studentLike.submittedById
    const candidateEmail = normalizeEmail(studentLike.studentEmail || studentLike.userEmail || studentLike.email)
    const resolved = (candidateId && studentDirectory.byId[candidateId]) || (candidateEmail && studentDirectory.byEmail[candidateEmail]) || null

    const preview = {
      studentId: resolved?.studentId || candidateId || '',
      name: resolved?.name || studentLike.studentName || studentLike.name || studentLike.studentEmail || 'Student',
      email: resolved?.email || studentLike.studentEmail || studentLike.userEmail || studentLike.email || '',
      avatar: resolved?.avatar || '',
      course: resolved?.course || ''
    }

    if (!preview.studentId) return

    navigate(`/student-profile/${preview.studentId}`, {
      state: {
        searchStudentPreview: preview
      }
    })
  }

  const updateEditField = (field, value) => {
    setEditForm((current) => ({ ...current, [field]: value }))
  }

  const handleEditAttachmentChange = (event) => {
    const nextFile = event.target.files?.[0] || null

    if (nextFile && nextFile.size > MAX_ASSIGNMENT_ATTACHMENT_SIZE) {
      setEditNotice('Keep the uploaded assignment file under 20 MB.')
      event.target.value = ''
      return
    }

    setEditForm((current) => ({
      ...current,
      nextAttachmentFile: nextFile,
      removeAttachment: nextFile ? false : current.removeAttachment
    }))

    if (editNotice === 'Keep the uploaded assignment file under 20 MB.') {
      setEditNotice('')
    }
  }

  const clearEditAttachmentInput = () => {
    if (editAttachmentInputRef.current) editAttachmentInputRef.current.value = ''
  }

  const removeCurrentAttachment = () => {
    clearEditAttachmentInput()
    setEditForm((current) => ({
      ...current,
      nextAttachmentFile: null,
      assignmentAttachment: null,
      removeAttachment: true
    }))
  }

  const keepCurrentAttachment = () => {
    clearEditAttachmentInput()
    setEditForm((current) => ({
      ...current,
      nextAttachmentFile: null,
      assignmentAttachment: getAssignmentAttachment(selectedAssignment),
      removeAttachment: false
    }))
  }

  const uploadAssignmentAttachment = async (file, instructorUid, courseKey) => {
    if (!file) return null
    if (!storage) {
      throw new Error('Storage is unavailable right now.')
    }

    const safeFileName = sanitizeStorageFileName(file.name || 'assignment-file')
    const storageRef = ref(
      storage,
      `assignment-resources/${instructorUid}/${courseKey || 'course'}/${Date.now()}_${safeFileName}`
    )
    const uploadTask = uploadBytesResumable(storageRef, file)

    await new Promise((resolve, reject) => {
      uploadTask.on('state_changed', undefined, reject, resolve)
    })

    const url = await getDownloadURL(uploadTask.snapshot.ref)

    return {
      name: file.name || safeFileName,
      url,
      path: uploadTask.snapshot.ref.fullPath,
      size: file.size || 0,
      contentType: file.type || '',
      uploadedAtMs: Date.now()
    }
  }

  const focusInstructionEditor = () => {
    instructionEditorRef.current?.focus()
  }

  const updateInstructionEditor = () => {
    const rawHtml = instructionEditorRef.current?.innerHTML || ''
    const cleanHtml = sanitizeRichTextHtml(rawHtml)
    setEditForm((current) => ({ ...current, instructions: cleanHtml }))
  }

  const applyInstructionStyle = (command) => {
    focusInstructionEditor()
    document.execCommand(command, false)
    updateInstructionEditor()
  }

  const insertInstructionList = () => {
    focusInstructionEditor()
    document.execCommand('insertUnorderedList', false)
    updateInstructionEditor()
  }

  const insertInstructionCode = () => {
    focusInstructionEditor()
    const selection = window.getSelection()
    const selectedText = selection ? selection.toString() : ''

    if (selectedText) {
      document.execCommand('insertHTML', false, `<code>${escapeHtml(selectedText)}</code>`)
    } else {
      document.execCommand('insertHTML', false, '<code>code</code>')
    }
    updateInstructionEditor()
  }

  const focusPromptEditor = (promptId) => {
    promptEditorRefs.current[promptId]?.focus()
  }

  const updatePromptItem = (promptId, value) => {
    setEditForm((current) => ({
      ...current,
      promptItems: normalizePromptItemsForEditor(current.promptItems).map((item) => (
        item.id === promptId ? { ...item, html: value } : item
      ))
    }))
  }

  const updatePromptEditor = (promptId) => {
    const rawHtml = promptEditorRefs.current[promptId]?.innerHTML || ''
    const cleanHtml = sanitizeRichTextHtml(rawHtml)
    updatePromptItem(promptId, cleanHtml)
  }

  const applyPromptStyle = (promptId, command) => {
    focusPromptEditor(promptId)
    document.execCommand(command, false)
    updatePromptEditor(promptId)
  }

  const insertPromptList = (promptId) => {
    focusPromptEditor(promptId)
    document.execCommand('insertUnorderedList', false)
    updatePromptEditor(promptId)
  }

  const insertPromptCode = (promptId) => {
    focusPromptEditor(promptId)
    const selection = window.getSelection()
    const selectedText = selection ? selection.toString() : ''

    if (selectedText) {
      document.execCommand('insertHTML', false, `<code>${escapeHtml(selectedText)}</code>`)
    } else {
      document.execCommand('insertHTML', false, '<code>code</code>')
    }
    updatePromptEditor(promptId)
  }

  const addPromptItem = () => {
    setEditForm((current) => ({
      ...current,
      promptItems: [...normalizePromptItemsForEditor(current.promptItems), createPromptItem('')]
    }))
  }

  const removePromptItem = (promptId) => {
    setEditForm((current) => {
      const nextPromptItems = normalizePromptItemsForEditor(current.promptItems).filter((item) => item.id !== promptId)
      delete promptEditorRefs.current[promptId]
      return {
        ...current,
        promptItems: nextPromptItems.length ? nextPromptItems : [createPromptItem('')]
      }
    })
  }

  const startEditing = () => {
    if (!selectedAssignment) return
    setEditForm(getEditFormFromAssignment(selectedAssignment))
    setIsEditing(true)
    setEditNotice('')
    setActiveAssignmentTab('overview')
    promptEditorRefs.current = {}
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setEditForm(getEditFormFromAssignment(selectedAssignment))
    setEditNotice('')
    promptEditorRefs.current = {}
    clearEditAttachmentInput()
  }

  const saveAssignmentChanges = async () => {
    if (!selectedAssignment?.id) return

    const assignmentTypeConfig = getAssignmentTypeConfig(editForm.assignmentType)
    const instructionsHtml = sanitizeRichTextHtml(editForm.instructions)
    const instructionsText = getPlainTextFromHtml(instructionsHtml)
    const promptItems = normalizePromptItemsForEditor(editForm.promptItems)
      .map((item) => sanitizeRichTextHtml(item.html || ''))
      .filter((item) => getPlainTextFromHtml(item))
    const promptItemsText = promptItems.map((item) => getPlainTextFromHtml(item)).filter(Boolean)

    if (!normalizeText(editForm.title)) {
      setEditNotice('Add an assignment title before saving.')
      return
    }

    if (assignmentTypeConfig?.supportsPromptItems && promptItems.length === 0) {
      setEditNotice('Add at least one question or prompt before saving.')
      return
    }

    if (!assignmentTypeConfig?.supportsPromptItems && !instructionsText) {
      setEditNotice('Add the assignment instructions before saving.')
      return
    }

    setSavingEdit(true)
    setEditNotice('')

    try {
      const nextAssignmentAttachment = editForm.nextAttachmentFile
        ? await uploadAssignmentAttachment(editForm.nextAttachmentFile, currentUserId, selectedAssignment.courseKey || selectedAssignment.courseName)
        : (editForm.removeAttachment ? null : getAssignmentAttachment(editForm))

      const payload = {
        title: normalizeText(editForm.title),
        assignmentType: editForm.assignmentType,
        assignmentTypeLabel: getAssignmentTypeLabel({ assignmentTypeLabel: '', assignmentType: editForm.assignmentType }),
        assignmentResponseMode: assignmentTypeConfig?.responseMode || 'text',
        dueDate: editForm.dueDate || '',
        resourceLink: normalizeText(editForm.resourceLink),
        assignmentAttachment: nextAssignmentAttachment,
        instructions: instructionsText,
        instructionsHtml,
        instructionsText,
        promptItems,
        promptItemsText,
        updatedAt: serverTimestamp(),
        updatedAtMs: Date.now()
      }

      await updateDoc(doc(db, 'courseAssignments', selectedAssignment.id), payload)

      setAssignments((current) => current.map((assignment) => (
        assignment.id === selectedAssignment.id
          ? { ...assignment, ...payload }
          : assignment
      )))

      setEditNotice('Assignment changes saved.')
      setIsEditing(false)
      clearEditAttachmentInput()
    } catch (error) {
      console.error('Error updating assignment:', error)
      setEditNotice('Could not save changes right now. Please try again.')
    } finally {
      setSavingEdit(false)
    }
  }

  const openAssignment = (assignmentId) => {
    setSelectedAssignmentId(assignmentId)
    setSelectedSubmissionId('')
    setIsAssignmentOpen(true)
    setIsEditing(false)
    setActiveAssignmentTab('overview')
    setReviewNotice('')
    setEditNotice('')
  }

  const openSubmissionReader = (submissionId) => {
    setSelectedSubmissionId(submissionId)
    setActiveAssignmentTab('submissions')
    setReviewNotice('')
  }

  const closeSubmissionReader = () => {
    setSelectedSubmissionId('')
    setReviewNotice('')
    setAssessmentError('')
  }

  const startSubmissionReview = async (forceRestart = false) => {
    if (!selectedSubmission || !selectedAssignment || !auth.currentUser || runningAssessment) return
    if (!forceRestart && (hasCompletedAssessment || selectedAssessment)) return

    setRunningAssessment(true)
    setAssessmentError('')
    setIsAssessmentOpen(true)
    setAssessmentBySubmission((current) => ({
      ...current,
      [selectedSubmission.id]: current[selectedSubmission.id]
        ? {
            ...current[selectedSubmission.id],
            status: 'processing',
            error: ''
          }
        : {
            id: selectedSubmission.id,
            status: 'processing'
          }
    }))
    setReviewNotice(`Generating a detailed AI learning review for ${selectedSubmission.studentName || selectedSubmission.studentEmail || 'this student'}. Please stay on this page until the review finishes.`)

    try {
      const token = await auth.currentUser.getIdToken()
      const response = await fetch(ASSIGNMENT_ASSESSMENT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          submissionId: selectedSubmission.id
        })
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(payload?.error || 'The AI review server could not process this submission right now.')
      }

      if (payload?.assessment) {
        const reviewedSubmissionPayload = {
          reviewStatus: 'reviewed',
          status: 'reviewed',
          aiAssessmentStatus: 'completed',
          aiAssessmentSummary: payload.assessment?.assessment?.summary?.headline || payload.assessment?.assessment?.summary?.overallAssessment || selectedSubmission.aiAssessmentSummary || '',
          reviewedAt: serverTimestamp(),
          reviewedAtMs: Date.now(),
          updatedAt: serverTimestamp(),
          updatedAtMs: Date.now()
        }

        await updateDoc(doc(db, 'assignmentSubmissions', selectedSubmission.id), reviewedSubmissionPayload)

        setAssessmentBySubmission((current) => ({
          ...current,
          [selectedSubmission.id]: payload.assessment
        }))
        setSubmissions((current) => current.map((submission) => (
          submission.id === selectedSubmission.id
            ? {
                ...submission,
                ...reviewedSubmissionPayload,
                reviewedAt: reviewedSubmissionPayload.reviewedAtMs,
                updatedAt: reviewedSubmissionPayload.updatedAtMs
              }
            : submission
        )))
        setIsAssessmentOpen(true)
      }

      setReviewNotice(`AI review complete for ${selectedSubmission.studentName || selectedSubmission.studentEmail || 'this student'}.`)
    } catch (error) {
      console.error('Error generating assignment assessment:', error)
      setAssessmentError(error.message || 'Could not generate the AI learning review right now.')
      setReviewNotice('')
    } finally {
      setRunningAssessment(false)
    }
  }

  if (loading) {
    return (
      <div className='assignmentsPage'>
        <div className='assignmentsState'>
          <div className='spinner-border text-dark' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
          <span>Loading assignments...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='assignmentsPage'>
      <section className='assignmentsHeader'>
        <div>
          <span>Assignment command center</span>
          <h2>Assignments</h2>
          <p>Track work across all courses, open any assignment in full, amend it, and review student submissions in one place.</p>
        </div>
      </section>

      <section className='assignmentsStats'>
        <div>
          <span>Total Assignments</span>
          <strong>{dashboardStats.total}</strong>
        </div>
        <div>
          <span>Pending Review</span>
          <strong>{dashboardStats.pendingReview}</strong>
        </div>
        <div>
          <span>Due Soon</span>
          <strong>{dashboardStats.dueSoon}</strong>
        </div>
        <div>
          <span>Overdue</span>
          <strong>{dashboardStats.overdue}</strong>
        </div>
      </section>

      <section className='assignmentsFilters'>
        <input
          type='text'
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder='Search assignment, course, task type...'
        />
        <select value={courseFilter} onChange={(event) => setCourseFilter(event.target.value)}>
          <option value='All'>All courses</option>
          {courses.map((course) => (
            <option value={course} key={course}>{course}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value='All'>All statuses</option>
          <option value='Pending Review'>Pending review</option>
          <option value='Due Soon'>Due soon</option>
          <option value='Overdue'>Overdue</option>
          <option value='Needs Revision'>Needs revision</option>
        </select>
      </section>

      {filteredAssignments.length > 0 ? (
        !isAssignmentOpen ? (
          <section className='assignmentsLauncher'>
            {filteredAssignments.map((assignment) => (
              <button
                type='button'
                className='assignmentsCard assignmentsCardButton assignmentsLauncherCard'
                key={assignment.id}
                onClick={() => openAssignment(assignment.id)}
              >
                <div className='assignmentsCardTop'>
                  <div>
                    <div className='assignmentsBadges'>
                      <span>{assignment.courseName || 'Course'}</span>
                      <span>{getAssignmentTypeLabel(assignment)}</span>
                      {getAssignmentWeekLabel(assignment) && <span>{getAssignmentWeekLabel(assignment)}</span>}
                    </div>
                    <h5>{assignment.title}</h5>
                  </div>
                  <strong className={assignment.overdue ? 'assignmentDueDanger' : ''}>
                    {formatDate(assignment.dueDate)}
                  </strong>
                </div>

                  <div className='assignmentsProgressGrid'>
                    <div>
                      <span>Selected</span>
                      <strong>{assignment.assignedCount}</strong>
                    </div>
                    <div>
                      <span>Opened</span>
                      <strong>{assignment.openedCount}</strong>
                    </div>
                    <div>
                      <span>Submitted</span>
                      <strong>{assignment.submittedCount}</strong>
                    </div>
                    <div>
                    <span>Pending</span>
                    <strong>{assignment.pendingCount}</strong>
                  </div>
                  <div>
                    <span>Reviewed</span>
                    <strong>{assignment.reviewedCount}</strong>
                  </div>
                </div>
              </button>
            ))}
          </section>
        ) : (
          <section className='assignmentsFullscreen'>
            <div className='assignmentsFullscreenBar'>
              <button
                type='button'
                className='assignmentsBackButton'
                onClick={() => {
                  setIsAssignmentOpen(false)
                  setIsEditing(false)
                  setReviewNotice('')
                  setEditNotice('')
                }}
              >
                Back to assignments
              </button>
            </div>

            <div className='assignmentsDetail assignmentsDetailFullscreen'>
              {selectedAssignment ? (
                <>
                  <div className='assignmentsDetailTop'>
                    <div>
                      <span className='assignmentsDetailEyebrow'>Assignment detail</span>
                      <h3>{selectedAssignment.title}</h3>
                      <p>{selectedAssignment.courseName || 'Course not set'}</p>
                    </div>
                    <div className='assignmentsDetailActions'>
                      {isEditing ? (
                        <>
                          <button type='button' className='assignmentsDetailGhost' onClick={cancelEditing}>Cancel</button>
                          <button type='button' className='assignmentsDetailPrimary' onClick={saveAssignmentChanges} disabled={savingEdit}>
                            {savingEdit ? 'Saving...' : 'Save changes'}
                          </button>
                        </>
                      ) : (
                        <button type='button' className='assignmentsDetailPrimary' onClick={startEditing}>Edit assignment</button>
                      )}
                    </div>
                  </div>

                  <div className='assignmentsDetailStats'>
                    <div>
                      <span>Due</span>
                      <strong>{formatDate(selectedAssignment.dueDate)}</strong>
                    </div>
                    <div>
                      <span>Type</span>
                      <strong>{getAssignmentTypeLabel(selectedAssignment)}</strong>
                    </div>
                    <div>
                      <span>Teaching plan</span>
                      <strong>{getAssignmentWeekLabel(selectedAssignment) || 'Not linked'}</strong>
                    </div>
                    <div>
                      <span>Audience</span>
                      <strong>{selectedAssignment.audience === 'selected' ? 'Selected students' : 'All students'}</strong>
                    </div>
                    <div>
                      <span>Opened</span>
                      <strong>{selectedAssignment.openedCount}/{selectedAssignment.assignedCount || 0}</strong>
                    </div>
                    <div>
                      <span>Submitted</span>
                      <strong>{selectedAssignment.submittedCount}/{selectedAssignment.assignedCount || 0}</strong>
                    </div>
                  </div>

                  <div className='assignmentsTabs'>
                    <button
                      type='button'
                      className={`assignmentsTabButton ${activeAssignmentTab === 'overview' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveAssignmentTab('overview')
                        setReviewNotice('')
                      }}
                    >
                      Overview
                    </button>
                    <button
                      type='button'
                      className={`assignmentsTabButton ${activeAssignmentTab === 'submissions' ? 'active' : ''}`}
                      onClick={() => setActiveAssignmentTab('submissions')}
                    >
                      Submissions
                    </button>
                  </div>

                  {editNotice && <div className='assignmentsEditNotice'>{editNotice}</div>}

                  {isEditing ? (
                    <div className='assignmentsEditorShell'>
                    <div className='assignmentsEditorGrid'>
                      <label className='assignmentsEditorField'>
                        <span>Assignment title</span>
                        <input
                          type='text'
                          value={editForm.title}
                          onChange={(event) => updateEditField('title', event.target.value)}
                        />
                      </label>

                      <label className='assignmentsEditorField'>
                        <span>Assignment type</span>
                        <select
                          value={editForm.assignmentType}
                          onChange={(event) => updateEditField('assignmentType', event.target.value)}
                        >
                          {assignmentTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <div className='assignmentsEditorGrid'>
                      <label className='assignmentsEditorField'>
                        <span>Due date</span>
                        <input
                          type='date'
                          value={editForm.dueDate}
                          onChange={(event) => updateEditField('dueDate', event.target.value)}
                        />
                      </label>

                      <label className='assignmentsEditorField'>
                        <span>Resource link</span>
                        <input
                          type='url'
                          value={editForm.resourceLink}
                          onChange={(event) => updateEditField('resourceLink', event.target.value)}
                          placeholder='https://...'
                        />
                      </label>

                      <div className='assignmentsEditorField'>
                        <span>Assignment file</span>
                        <input
                          ref={editAttachmentInputRef}
                          type='file'
                          onChange={handleEditAttachmentChange}
                        />
                        {editForm.nextAttachmentFile ? (
                          <p className='assignmentsEditNotice'>
                            New file ready: {editForm.nextAttachmentFile.name}{' '}
                            {formatFileSize(editForm.nextAttachmentFile.size) ? `(${formatFileSize(editForm.nextAttachmentFile.size)})` : ''}
                          </p>
                        ) : editForm.assignmentAttachment && !editForm.removeAttachment ? (
                          <p className='assignmentsEditNotice'>
                            Current file: {editForm.assignmentAttachment.name}
                            {formatFileSize(editForm.assignmentAttachment.size) ? ` (${formatFileSize(editForm.assignmentAttachment.size)})` : ''}
                          </p>
                        ) : (
                          <p className='assignmentsEditNotice'>No assignment file attached.</p>
                        )}
                        {(editForm.assignmentAttachment || editForm.nextAttachmentFile || editForm.removeAttachment) && (
                          <div className='assignmentsDetailActions'>
                            {editForm.assignmentAttachment && !editForm.removeAttachment && (
                              <button type='button' className='assignmentsDetailGhost' onClick={removeCurrentAttachment}>
                                Remove current file
                              </button>
                            )}
                            {editForm.removeAttachment && selectedAssignment?.assignmentAttachment && (
                              <button type='button' className='assignmentsDetailGhost' onClick={keepCurrentAttachment}>
                                Restore current file
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='assignmentsEditorField'>
                      <span>{getAssignmentTypeConfig(editForm.assignmentType)?.supportsPromptItems ? 'Assignment brief' : 'Instructions'}</span>
                      <div className='instructorAssignmentEditorShell'>
                        <div className='instructorAssignmentEditorTools'>
                          <button type='button' onClick={() => applyInstructionStyle('bold')} aria-label='Bold'><FontAwesomeIcon icon={faBold} /></button>
                          <button type='button' onClick={() => applyInstructionStyle('underline')} aria-label='Underline'><FontAwesomeIcon icon={faUnderline} /></button>
                          <button type='button' onClick={insertInstructionList} aria-label='List'><FontAwesomeIcon icon={faListUl} /></button>
                          <button type='button' onClick={insertInstructionCode} aria-label='Code'><FontAwesomeIcon icon={faCode} /></button>
                        </div>
                        <div
                          className='instructorAssignmentEditor'
                          ref={instructionEditorRef}
                          contentEditable
                          suppressContentEditableWarning
                          role='textbox'
                          aria-multiline='true'
                          data-placeholder='Write the assignment brief or instructions...'
                          onInput={updateInstructionEditor}
                        />
                      </div>
                    </div>

                    {getAssignmentTypeConfig(editForm.assignmentType)?.supportsPromptItems && (
                      <div className='instructorAssignmentPromptBuilder'>
                        <div className='instructorAssignmentPromptHeader'>
                          <div>
                            <label>Questions or prompts</label>
                            <p>Edit the prompts exactly as students should see them.</p>
                          </div>
                          <button type='button' className='instructorAssignmentPromptAdd' onClick={addPromptItem}>
                            Add question
                          </button>
                        </div>

                        <div className='instructorAssignmentPromptList'>
                          {editForm.promptItems.map((prompt, index) => (
                            <div className='instructorAssignmentPromptRow' key={prompt.id}>
                              <div className='instructorAssignmentPromptTop'>
                                <span>Q{index + 1}</span>
                                <div className='instructorAssignmentEditorTools'>
                                  <button type='button' onClick={() => applyPromptStyle(prompt.id, 'bold')} aria-label='Bold'><FontAwesomeIcon icon={faBold} /></button>
                                  <button type='button' onClick={() => applyPromptStyle(prompt.id, 'underline')} aria-label='Underline'><FontAwesomeIcon icon={faUnderline} /></button>
                                  <button type='button' onClick={() => insertPromptList(prompt.id)} aria-label='List'><FontAwesomeIcon icon={faListUl} /></button>
                                  <button type='button' onClick={() => insertPromptCode(prompt.id)} aria-label='Code'><FontAwesomeIcon icon={faCode} /></button>
                                </div>
                              </div>
                              <div
                                className='instructorAssignmentPromptEditor'
                                ref={(node) => {
                                  if (node) {
                                    promptEditorRefs.current[prompt.id] = node
                                    if (node.innerHTML !== (prompt.html || '')) node.innerHTML = prompt.html || ''
                                  } else {
                                    delete promptEditorRefs.current[prompt.id]
                                  }
                                }}
                                contentEditable
                                suppressContentEditableWarning
                                role='textbox'
                                aria-multiline='true'
                                data-placeholder='Write the prompt here...'
                                onInput={() => updatePromptEditor(prompt.id)}
                              />
                              <button
                                type='button'
                                className='instructorAssignmentPromptRemove'
                                onClick={() => removePromptItem(prompt.id)}
                                disabled={editForm.promptItems.length <= 1}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </div>
                  ) : activeAssignmentTab === 'overview' ? (
                    <>
                      <div className='assignmentsOverviewPanel'>
                        <div className='assignmentsOverviewHeader'>
                          <div>
                            <span className='assignmentsDetailEyebrow'>Assignment brief</span>
                            <h4>What students received</h4>
                          </div>
                          <div className='assignmentsOverviewCount'>
                            {selectedAssignment.promptItems?.length || 0} question{selectedAssignment.promptItems?.length === 1 ? '' : 's'}
                          </div>
                        </div>

                        {selectedAssignment.promptItems?.length > 0 && (
                          <div className='assignmentsPromptOverviewList'>
                            {selectedAssignment.promptItems.map((prompt, index) => (
                              <div
                                className='assignmentRichText assignmentPromptViewItem'
                                key={`${selectedAssignment.id}-prompt-${index}`}
                                dangerouslySetInnerHTML={{
                                  __html: `<span>Question ${index + 1}</span>${sanitizeRichTextHtml(prompt)}`
                                }}
                              />
                            ))}
                          </div>
                        )}

                        <div
                          className='assignmentRichText assignmentsDetailRichText'
                          dangerouslySetInnerHTML={{
                            __html: sanitizeRichTextHtml(selectedAssignment.instructionsHtml || selectedAssignment.instructions || '') || '<p>No general brief added beyond the questions below.</p>'
                          }}
                        />

                        {selectedAssignment.resourceLink && (
                          <a className='assignmentsResourceLink' href={selectedAssignment.resourceLink} target='_blank' rel='noreferrer'>
                            Open resource link
                          </a>
                        )}

                        {getAssignmentAttachment(selectedAssignment) && (
                          <a
                            className='assignmentsResourceLink'
                            href={getAssignmentAttachment(selectedAssignment).url}
                            target='_blank'
                            rel='noreferrer'
                            download={getAssignmentAttachment(selectedAssignment).name}
                          >
                            Download {getAssignmentAttachment(selectedAssignment).name}
                          </a>
                        )}

                        <div className='assignmentsReadPanel'>
                          <div className='assignmentsOverviewHeader'>
                            <div>
                              <span className='assignmentsDetailEyebrow'>Student activity</span>
                              <h4>Opened by</h4>
                            </div>
                            <div className='assignmentsOverviewCount'>
                              {selectedAssignment.openedCount} opened • {selectedAssignment.unopenedCount} not yet
                            </div>
                          </div>

                          {selectedAssignment.openedBy?.length > 0 ? (
                            <div className='assignmentsReadList'>
                              {selectedAssignment.openedBy.map((readEntry) => (
                                <div className='assignmentsReadCard' key={readEntry.id || `${selectedAssignment.id}-${readEntry.studentId}`}>
                                  <div>
                                    <strong>{readEntry.studentName || 'Student'}</strong>
                                    <p>{readEntry.studentEmail || readEntry.studentCourse || 'Student account'}</p>
                                  </div>
                                  <span>{formatDateTime(readEntry.readAtMs || readEntry.readAt)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className='assignmentsMuted'>No student has opened this assignment yet.</p>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className={`assignmentsSubmissionWorkspace ${selectedSubmission ? 'reader-open' : ''}`}>
                      {!selectedSubmission ? (
                        <div className='assignmentsSubmissionRail assignmentsSubmissionRailFull'>
                          <div className='assignmentsSubmissionHeader'>
                            <div>
                              <span className='assignmentsDetailEyebrow'>Student submissions</span>
                              <h4>{selectedAssignment.submittedCount} of {selectedAssignment.assignedCount || 0} submitted</h4>
                              <p>Open any learner submission to read it in full and prepare for review.</p>
                            </div>
                          </div>

                          {selectedAssignment.submissions.length > 0 ? (
                            <div className='assignmentsSubmissionRailList'>
                              {selectedAssignment.submissions.map((submission) => (
                                <button
                                  type='button'
                                  key={submission.id}
                                  className='assignmentsSubmissionRailCard'
                                  onClick={() => openSubmissionReader(submission.id)}
                                >
                                  <div>
                                    <strong>{submission.studentName || submission.name || submission.studentEmail || 'Student'}</strong>
                                    <p>{formatDateTime(submission.updatedAt || submission.submittedAt || submission.createdAt)}</p>
                                  </div>
                                  <span className={`assignmentsSubmissionStatus assignmentsSubmissionStatus-${getSubmissionStatus(submission).includes('review') ? 'reviewed' : getSubmissionStatus(submission).includes('revision') ? 'revision' : 'submitted'}`}>
                                    {submission.reviewStatus || submission.status || 'submitted'}
                                  </span>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <p className='assignmentsMuted'>No submissions yet for this assignment.</p>
                          )}
                        </div>
                      ) : (
                        <div className='assignmentsSubmissionReader assignmentsSubmissionReaderFull'>
                          <div className='assignmentsSubmissionTop'>
                            <div>
                              <span className='assignmentsDetailEyebrow'>Submission reader</span>
                              <button
                                type='button'
                                className='studentProfileLinkButton assignmentsSubmissionStudentLink'
                                onClick={() => openStudentProfile(selectedSubmission)}
                              >
                                {selectedSubmission.studentName || selectedSubmission.name || selectedSubmission.studentEmail || 'Student'}
                              </button>
                              <p>{formatDateTime(selectedSubmission.updatedAt || selectedSubmission.submittedAt || selectedSubmission.createdAt)}</p>
                            </div>
                            <div className='assignmentsDetailActions'>
                              <button type='button' className='assignmentsDetailGhost' onClick={closeSubmissionReader}>
                                Back to submissions
                              </button>
                              <span className={`assignmentsSubmissionStatus assignmentsSubmissionStatus-${getSubmissionStatus(selectedSubmission).includes('review') ? 'reviewed' : getSubmissionStatus(selectedSubmission).includes('revision') ? 'revision' : 'submitted'}`}>
                                {selectedSubmission.reviewStatus || selectedSubmission.status || 'submitted'}
                              </span>
                              {!selectedAssessment && (
                                <button
                                  type='button'
                                  className='assignmentsDetailPrimary assignmentsSubmissionReviewButton'
                                  onClick={() => startSubmissionReview(false)}
                                  disabled={runningAssessment}
                                >
                                  {runningAssessment ? 'Review in progress...' : 'Review submission'}
                                </button>
                              )}
                              {canRunNewReview && (
                                <button
                                  type='button'
                                  className='assignmentsDetailGhost assignmentsIconButton'
                                  onClick={() => startSubmissionReview(true)}
                                  disabled={runningAssessment}
                                  aria-label='Run new review'
                                  title='Run new review'
                                >
                                  <FontAwesomeIcon icon={faRotateRight} />
                                </button>
                              )}
                              {hasCompletedAssessment && (
                                <button
                                  type='button'
                                  className='assignmentsDetailPrimary assignmentsSubmissionReviewButton assignmentsSubmissionReviewButtonView'
                                  onClick={() => setIsAssessmentOpen((current) => !current)}
                                >
                                  {isAssessmentOpen ? 'Hide learner review' : 'View learner review'}
                                </button>
                              )}
                            </div>
                          </div>

                          {reviewNotice && (
                            <div className='assignmentsReviewNotice'>{reviewNotice}</div>
                          )}

                          {assessmentError && (
                            <div className='assignmentsReviewNotice assignmentsReviewNoticeError'>{assessmentError}</div>
                          )}

                          {(assessmentLoading || runningAssessment || selectedAssessment) && (runningAssessment || assessmentLoading || isAssessmentOpen) && (
                            <div className='assignmentsAssessmentPanel'>
                              <div className='assignmentsOverviewHeader'>
                                <div>
                                  <span className='assignmentsDetailEyebrow'>AI learning review</span>
                                  <h4>{selectedAssessment?.assessment?.summary?.overallSignal || (runningAssessment ? 'Building assessment...' : 'Saved assessment')}</h4>
                                </div>
                                <div className='assignmentsOverviewCount'>
                                  {runningAssessment
                                    ? 'Processing'
                                    : selectedAssessment?.assessment
                                      ? 'Review ready'
                                      : 'Awaiting run'}
                                </div>
                              </div>

                              {assessmentLoading && !selectedAssessment ? (
                                <p className='assignmentsMuted'>Loading the saved assessment...</p>
                              ) : selectedAssessment?.status === 'failed' ? (
                                <p className='assignmentsMuted'>{selectedAssessment.error || 'The last AI assessment run failed.'}</p>
                              ) : selectedAssessment?.assessment ? (
                                <div className='assignmentsAssessmentGrid'>
                                  <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                    <span>Instructor summary</span>
                                    <h5>{selectedAssessment.assessment.summary?.headline || 'Detailed learner assessment'}</h5>
                                    <p>{selectedAssessment.assessment.summary?.learnerSnapshot || selectedAssessment.assessment.summary?.futureOfLearningLens || 'No summary was generated yet.'}</p>
                                    <div className='assignmentsAssessmentMeta'>
                                      <strong>{selectedAssessment.assessment.summary?.confidence || 'Confidence pending'}</strong>
                                      <span>{selectedAssessment.assessment.summary?.confidenceReason || selectedAssessment.assessment.summary?.evidenceCoverage || 'Evidence coverage will appear here.'}</span>
                                    </div>
                                  </article>

                                  <article className='assignmentsAssessmentCard'>
                                    <span>Future readiness</span>
                                    <h5>{selectedAssessment.assessment.summary?.futureReadiness || 'Signal pending'}</h5>
                                    <p>{selectedAssessment.assessment.summary?.overallAssessment || 'The system will explain the learner’s trajectory here.'}</p>
                                  </article>

                                  {normalizeText(selectedAssessment.assessment.summary?.curriculumAlignment) && (
                                    <article className='assignmentsAssessmentCard'>
                                      <span>Curriculum alignment</span>
                                      <h5>{selectedAssessment.curriculumSnapshot?.selectedWeek?.label || getAssignmentWeekLabel(selectedAssignment) || 'Selected teaching-plan week'}</h5>
                                      <p>{selectedAssessment.assessment.summary.curriculumAlignment}</p>
                                    </article>
                                  )}

                                  {rubricChartItems.length > 0 && (
                                    <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                      <span>Performance snapshot</span>
                                      <h5>Rubric score profile</h5>
                                      <div className='assignmentsAssessmentChart' aria-label='Rubric performance chart'>
                                        {rubricChartItems.map((item) => (
                                          <div key={item.id} className='assignmentsAssessmentChartColumn'>
                                            <strong>{renderAssessmentScore(item.score, item.outOf)}</strong>
                                            <div className='assignmentsAssessmentChartBar'>
                                              <div style={{ height: `${Math.max(item.percent, 10)}%` }} />
                                            </div>
                                            <span title={item.label}>{item.shortLabel}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </article>
                                  )}

                                  {normalizeAssessmentList(selectedAssessment.assessment.rubric).length > 0 && (
                                    <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                      <span>Rubric dimensions</span>
                                      <div className='assignmentsAssessmentList'>
                                        {selectedAssessment.assessment.rubric.map((item, index) => (
                                          <div key={`${item.id || item.label}-${index}`}>
                                            <strong>{item.label} • {renderAssessmentScore(item.score, item.outOf || 5)}</strong>
                                            <div className='assignmentsAssessmentMeter' aria-hidden='true'>
                                              <div style={{ width: `${getAssessmentScorePercent(item.score, item.outOf || 5)}%` }} />
                                            </div>
                                            <p>{item.rationale}</p>
                                            {item.nextMove && <small>Next move: {item.nextMove}</small>}
                                          </div>
                                        ))}
                                      </div>
                                    </article>
                                  )}

                                  {normalizeAssessmentList(selectedAssessment.assessment.strengths).length > 0 && (
                                    <article className='assignmentsAssessmentCard'>
                                      <span>Strengths</span>
                                      <div className='assignmentsAssessmentList'>
                                        {selectedAssessment.assessment.strengths.map((item, index) => (
                                          <div key={`${item.title}-${index}`}>
                                            <strong>{item.title}</strong>
                                            <p>{item.whyItMatters}</p>
                                            {item.evidence && <small>Evidence: {item.evidence}</small>}
                                          </div>
                                        ))}
                                      </div>
                                    </article>
                                  )}

                                  {normalizeAssessmentList(selectedAssessment.assessment.growthAreas).length > 0 && (
                                    <article className='assignmentsAssessmentCard'>
                                      <span>Growth areas</span>
                                      <div className='assignmentsAssessmentList'>
                                        {selectedAssessment.assessment.growthAreas.map((item, index) => (
                                          <div key={`${item.title}-${index}`}>
                                            <strong>{item.title}</strong>
                                            <p>{item.whyItMatters}</p>
                                            {item.intervention && <small>Intervention: {item.intervention}</small>}
                                          </div>
                                        ))}
                                      </div>
                                    </article>
                                  )}

                                  {normalizeAssessmentList(selectedAssessment.assessment.misconceptions).length > 0 && (
                                    <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                      <span>Misconceptions and teaching moves</span>
                                      <div className='assignmentsAssessmentList'>
                                        {selectedAssessment.assessment.misconceptions.map((item, index) => (
                                          <div key={`${item.concept}-${index}`}>
                                            <strong>{item.concept}</strong>
                                            <p>{item.correction}</p>
                                            {item.teachingMove && <small>Teaching move: {item.teachingMove}</small>}
                                          </div>
                                        ))}
                                      </div>
                                    </article>
                                  )}

                                  {normalizeAssessmentList(selectedAssessment.assessment.promptFindings).length > 0 && (
                                    <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                      <span>Prompt-by-prompt findings</span>
                                      <div className='assignmentsAssessmentList'>
                                        {selectedAssessment.assessment.promptFindings.map((item, index) => (
                                          <div key={`${item.promptLabel || item.promptNumber}-${index}`}>
                                            <strong>{item.promptLabel || `Prompt ${index + 1}`} • {renderAssessmentScore(item.score, 5)}</strong>
                                            <div className='assignmentsAssessmentMeter' aria-hidden='true'>
                                              <div style={{ width: `${getAssessmentScorePercent(item.score, 5)}%` }} />
                                            </div>
                                            <p>{item.responseSummary || item.masterySignal}</p>
                                            {item.coachingMove && <small>Coaching move: {item.coachingMove}</small>}
                                          </div>
                                        ))}
                                      </div>
                                    </article>
                                  )}

                                  {(normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.immediateActions).length > 0
                                    || normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.nextAssignmentAdjustments).length > 0
                                    || normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.futureReadyNextSteps).length > 0) && (
                                    <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                      <span>Teaching plan</span>
                                      <div className='assignmentsAssessmentBulletGrid'>
                                        {normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.immediateActions).length > 0 && (
                                          <div>
                                            <strong>Immediate instructor actions</strong>
                                            {selectedAssessment.assessment.teachingPlan.immediateActions.map((item, index) => <p key={`action-${index}`}>{item}</p>)}
                                          </div>
                                        )}
                                        {normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.nextAssignmentAdjustments).length > 0 && (
                                          <div>
                                            <strong>Next assignment adjustments</strong>
                                            {selectedAssessment.assessment.teachingPlan.nextAssignmentAdjustments.map((item, index) => <p key={`adjustment-${index}`}>{item}</p>)}
                                          </div>
                                        )}
                                        {normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.futureReadyNextSteps).length > 0 && (
                                          <div>
                                            <strong>Future-ready next steps</strong>
                                            {selectedAssessment.assessment.teachingPlan.futureReadyNextSteps.map((item, index) => <p key={`future-${index}`}>{item}</p>)}
                                          </div>
                                        )}
                                      </div>
                                    </article>
                                  )}

                                  {(normalizeAssessmentList(selectedAssessment.assessment.studentFeedback?.celebration).length > 0
                                    || normalizeAssessmentList(selectedAssessment.assessment.studentFeedback?.improve).length > 0
                                    || normalizeText(selectedAssessment.assessment.studentFeedback?.nextChallenge)) && (
                                    <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                      <span>Student-facing feedback</span>
                                      <div className='assignmentsAssessmentBulletGrid'>
                                        {normalizeAssessmentList(selectedAssessment.assessment.studentFeedback?.celebration).length > 0 && (
                                          <div>
                                            <strong>Celebrate</strong>
                                            {selectedAssessment.assessment.studentFeedback.celebration.map((item, index) => <p key={`celebrate-${index}`}>{item}</p>)}
                                          </div>
                                        )}
                                        {normalizeAssessmentList(selectedAssessment.assessment.studentFeedback?.improve).length > 0 && (
                                          <div>
                                            <strong>Improve</strong>
                                            {selectedAssessment.assessment.studentFeedback.improve.map((item, index) => <p key={`improve-${index}`}>{item}</p>)}
                                          </div>
                                        )}
                                        {normalizeText(selectedAssessment.assessment.studentFeedback?.nextChallenge) && (
                                          <div>
                                            <strong>Next challenge</strong>
                                            <p>{selectedAssessment.assessment.studentFeedback.nextChallenge}</p>
                                          </div>
                                        )}
                                      </div>
                                    </article>
                                  )}
                                </div>
                              ) : runningAssessment ? (
                                <p className='assignmentsMuted'>The AI is analysing the submission, assignment brief, and learner evidence now.</p>
                              ) : (
                                <p className='assignmentsMuted'>Run the AI review to generate a detailed learner analysis for this submission.</p>
                              )}
                            </div>
                          )}

                          {Array.isArray(selectedAssignment.promptItems) && selectedAssignment.promptItems.length > 0 && (
                            <div className='assignmentsSubmissionDetailGroup'>
                              {selectedAssignment.promptItems.map((prompt, index) => {
                                const answerText = selectedSubmission.responseItems?.[index] || ''
                                const answerHtml = getSubmissionResponseHtmlItems(selectedSubmission)[index] || ''
                                const hasAnswer = normalizeText(answerText) || normalizeText(answerHtml)

                                return (
                                  <div className='assignmentsSubmissionQuestionBlock' key={`${selectedSubmission.id}-response-${index}`}>
                                    <div
                                      className='assignmentRichText assignmentPromptViewItem'
                                      dangerouslySetInnerHTML={{
                                        __html: `<span>Question ${index + 1}</span>${sanitizeRichTextHtml(prompt)}`
                                      }}
                                    />
                                    <div className='assignmentsSubmissionAnswer'>
                                      <span>Student response</span>
                                      {hasAnswer ? (
                                        answerHtml ? (
                                          <div
                                            className='assignmentRichText'
                                            dangerouslySetInnerHTML={{ __html: answerHtml }}
                                          />
                                        ) : ['questions_code', 'code_paste'].includes(selectedAssignment.assignmentType) ? <pre>{answerText}</pre> : <p>{answerText}</p>
                                      ) : (
                                        <p>No response was submitted for this question.</p>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}

                          {!getAssignmentTypeConfig(selectedAssignment.assignmentType)?.supportsPromptItems && (normalizeText(selectedSubmission.responseText || selectedSubmission.answerText) || normalizeText(selectedSubmission.responseTextHtml)) && (
                            <div className='assignmentsSubmissionAnswer'>
                              <span>{['questions_code', 'code_paste'].includes(selectedAssignment.assignmentType) ? 'Submitted code' : 'Submitted response'}</span>
                              {selectedSubmission.responseTextHtml ? (
                                <div
                                  className='assignmentRichText'
                                  dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(selectedSubmission.responseTextHtml) }}
                                />
                              ) : ['questions_code', 'code_paste'].includes(selectedAssignment.assignmentType)
                                ? <pre>{normalizeText(selectedSubmission.responseText || selectedSubmission.answerText)}</pre>
                                : <p>{normalizeText(selectedSubmission.responseText || selectedSubmission.answerText)}</p>}
                            </div>
                          )}

                          {(normalizeText(selectedSubmission.notes) || normalizeText(selectedSubmission.notesHtml)) && (
                            <div className='assignmentsSubmissionAnswer'>
                              <span>Student notes</span>
                              {selectedSubmission.notesHtml ? (
                                <div
                                  className='assignmentRichText'
                                  dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(selectedSubmission.notesHtml) }}
                                />
                              ) : <p>{normalizeText(selectedSubmission.notes)}</p>}
                            </div>
                          )}

                          {getSubmissionLinks(selectedSubmission).length > 0 && (
                            <div className='assignmentsSubmissionLinksBlock'>
                              <span className='assignmentsDetailEyebrow'>Shared links</span>
                              <div className='assignmentsSubmissionLinks'>
                                {getSubmissionLinks(selectedSubmission).map((link) => (
                                  <a
                                    key={`${selectedSubmission.id}-${link.label}`}
                                    className='assignmentsSubmissionLinkCard'
                                    href={link.value}
                                    target='_blank'
                                    rel='noreferrer'
                                  >
                                    <span>{link.label}</span>
                                    <strong>{link.value}</strong>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className='assignmentsState'>
                  <h5>No assignment selected</h5>
                  <p>Select an assignment from the list to open it in full and review student submissions.</p>
                </div>
              )}
            </div>
          </section>
        )
      ) : (
        <section className='assignmentsState'>
          <h5>No assignments found</h5>
          <p>{loadMessage || 'Create assignments from a course page, then track them here across all classes.'}</p>
        </section>
      )}

      {runningAssessment && (
        <div className='assignmentsReviewOverlay' role='alert' aria-live='assertive' aria-busy='true'>
          <div className='assignmentsReviewOverlayCard'>
            <span>AI review in progress</span>
            <h4>Please do not leave this page</h4>
            <p>
              We are reviewing {selectedSubmission?.studentName || selectedSubmission?.name || selectedSubmission?.studentEmail || 'this submission'}.
              The page is temporarily locked until the assessment is generated and saved.
            </p>
            <div className='assignmentsReviewOverlayBar' aria-hidden='true'>
              <div />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AssignmentsComp
